import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlane,
  faUser,
  faCalendar,
  faLocationDot,
  faNoteSticky,
  faMoneyBill,
  faFile,
  faCheckCircle,
  faClose,
} from "@fortawesome/free-solid-svg-icons";
import defaultAvatar from "../../../assets/images/avatar/employee-1.png";
import { formatDateVN, formatDateTime } from "../../../utils/formatUtils";

const BusinessTripDetailModal = ({ isOpen, onClose, data }) => {
  // ESC để đóng
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen || !data) return null;

  return (
    <div
      className="fixed inset-0 bg-black/45 backdrop-blur-sm flex items-center justify-center z-[999]"
      onClick={onClose} // ← click nền đóng modal
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[600px] max-w-[95%] overflow-hidden border animate-[popupShow_0.25s_ease-out]"
        onClick={(e) => e.stopPropagation()} // ← chặn lan ra ngoài
      >

        {/* ================= HEADER ================= */}
        <div className="px-6 py-4 flex justify-between items-center border-b">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faPlane} className="text-gray-800" />
            Chi tiết công tác
          </h3>

          <button onClick={onClose}>
            <FontAwesomeIcon icon={faClose} className="text-gray-600" />
          </button>
        </div>

        {/* ================= CONTENT ================= */}
        <div className="px-6 py-5 space-y-6 max-h-[75vh] overflow-y-auto">

          {/* ========== PHẦN 1: THÔNG TIN NHÂN VIÊN ========== */}
          <div className="border rounded-xl p-4 bg-gray-50 shadow-sm space-y-4">
            <div className="flex gap-4 items-center">
              <img
                src={data.employee_avatar || defaultAvatar}
                alt=""
                className="w-16 h-16 rounded-full border shadow-sm object-cover"
              />

              <div>
                <div className="text-[17px] font-semibold text-gray-900">
                  {data.employee_name}
                </div>
                <div className="text-sm text-gray-600">
                  {data.branch_name} • {data.department_name}
                </div>

                <span className="mt-2 inline-block text-xs px-2 py-[3px] rounded-full font-semibold">
                  {data.status === "Đã duyệt" ? (
                    <span className="ml-2 text-xs font-medium bg-green-100 text-green-700 border border-green-300 px-2 py-1 rounded-full">
                      {data.status}
                    </span>
                  ) : data.status === "Không duyệt" ? (
                    <span className="ml-2 text-xs font-medium bg-rose-100 text-rose-700 border border-rose-300 px-2 py-1 rounded-full">
                      {data.status}
                    </span>
                  ) : (
                    <span className="ml-2 text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-300 px-2 py-1 rounded-full">
                      {data.status || "Chờ duyệt"}
                    </span>
                  )}
                </span>

              </div>
            </div>
          </div>

          {/* ========== PHẦN 2: THÔNG TIN CÔNG TÁC ========== */}
          <div className="border rounded-xl p-4 bg-white shadow-sm space-y-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <FontAwesomeIcon icon={faLocationDot} className="text-blue-600" />
              Thông tin công tác
            </h3>

            <div className="grid grid-cols-[24px_120px_1fr] gap-y-2 text-sm text-gray-800">

              {/* Ngày bắt đầu */}
              <div className="flex items-center">
                <FontAwesomeIcon icon={faCalendar} className="text-gray-500" />
              </div>
              <div className="font-medium text-gray-500">Ngày bắt đầu:</div>
              <div className="font-semibold">{formatDateVN(data.start_date)}</div>

              {/* Ngày kết thúc */}
              <div className="flex items-center">
                <FontAwesomeIcon icon={faCalendar} className="text-gray-500" />
              </div>
              <div className="font-medium text-gray-500">Ngày kết thúc:</div>
              <div className="font-semibold">{formatDateVN(data.end_date)}</div>

              {/* Địa điểm */}
              <div className="flex items-start">
                <FontAwesomeIcon icon={faLocationDot} className="text-gray-500" />
              </div>
              <div className="font-medium text-gray-500">Địa điểm:</div>
              <div className="font-semibold">{data.location}</div>

              {/* Lý do */}
              <div className="flex items-start">
                <FontAwesomeIcon icon={faNoteSticky} className="text-gray-500" />
              </div>
              <div className="font-medium text-gray-500">Lý do:</div>
              <div className="font-semibold">{data.reason || "-"}</div>

              {/* Ngân sách */}
              <div className="flex items-center">
                <FontAwesomeIcon icon={faMoneyBill} className="text-gray-500" />
              </div>
              <div className="font-medium text-gray-500">Ngân sách:</div>
              <div className="font-semibold">
                {data.budget_cost
                  ? Number(data.budget_cost).toLocaleString("vi-VN") + " VNĐ"
                  : "0 VNĐ"}
              </div>

              {/* File đính kèm */}
              {data.attached_file && (
                <>
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faFile} className="text-gray-500" />
                  </div>
                  <div className="font-medium text-gray-500">File đính kèm:</div>
                  <div>
                    <a
                      href={data.attached_file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Xem file
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ========== PHẦN 3: PHÊ DUYỆT ========== */}
          {data.status !== "pending" && (
            <div className="border rounded-xl p-4 bg-gray-50 shadow-sm space-y-3">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                Thông tin phê duyệt
              </h3>

              <div className="text-sm space-y-1">
                <div>
                  <b>Người duyệt:</b> {data.approver_name || "-"}
                </div>
                <div>
                  <b>Thời gian duyệt:</b> {formatDateTime(data.approved_at)}
                </div>
              </div>

              {data.status === "rejected" && data.reject_reason && (
                <p className="text-red-600 text-sm">
                  <b>Lý do từ chối:</b> {data.reject_reason}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Animation */}
      <style>{`
        @keyframes popupShow {
          0% { transform: scale(0.94); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default BusinessTripDetailModal;
